import { useState, useEffect } from 'react';

import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import MonthlySalesDetails from 'components/table/MonthlySalesDetails';
import SalesDetails from 'components/table/SalesDetails';
import { QueryKeys, restfetcher } from 'queryClient';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import {
  convertToEndDate,
  convertToStartDate,
  getMonthStartAndEndDates,
  getTodayStartAndEndDates
} from 'utils/date';

interface SalesProps {
  companyInfo: CompanyInfor;
}

const selectSearch = [
  {
    key: 1,
    text: '검색대상을 선택해주세요.',
    value: '전체'
  },
  {
    key: 2,
    text: '담당자명을 입력해주세요.',
    value: '담당자'
  },
  {
    key: 3,
    text: '카드명을 입력해주세요.',
    value: '거래카드'
  },
  {
    key: 4,
    text: '4자리 숫자를 입력해주세요.',
    value: '카드번호'
  }
];

const Sales = ({ companyInfo }: SalesProps) => {
  const pageSize = 10;

  const [pageNumber, setPageNumber] = useState(0);
  const [startDate, setStartDate] = useState<string | null>('');
  const [endDate, setEndDate] = useState<string | null>('');
  const [searchState, setSearchState] = useState(selectSearch[0]);
  const [startDateForParam, setStartDateForParam] = useState<string | null>('');
  const [endDateForParam, setEndDateForParam] = useState<string | null>('');
  const [activePeriodButton, setActivePeriodButton] = useState(0);
  const [searchOption, setSearchOption] = useState<string | number>('none');
  const [searchKeyword, setSearchKeyword] = useState('');

  const selectSearchOptions: SelectOption[] = [
    { value: 'manager', label: '담당자' },
    { value: 'cardname', label: '거래카드' },
    { value: 'cardnum', label: '카드번호' }
  ];

  const salesManagementApi = useQuery({
    queryKey: [QueryKeys.SALES_MANAGEMENT],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/sales/find',
        params: {
          companyidx: companyInfo.companyidx,
          page: pageNumber,
          size: pageSize,
          searchtype: searchOption,
          keyword: searchKeyword,
          startdate: startDateForParam,
          enddate: endDateForParam
        }
      })
  });

  const { data: salesData } = salesManagementApi;

  const salesList: SalesList[] = salesData?.data?.list;
  const totalElements = salesData?.data?.totalElements;
  const totalPages = salesData?.data?.totalPages;
  const currentPage = salesData?.data?.currentPage;

  const requestSalesData = () => {
    salesManagementApi.refetch();
  };

  const handleChangeSearchKeyword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(event.target.value);
  };

  const handleClickTotalDate = () => {
    setStartDateForParam('');
    setEndDateForParam('');
    setStartDate('');
    setEndDate('');
  };

  const handleClickThisMonthDate = () => {
    const currentMonthDates = getMonthStartAndEndDates();

    const { startOfMonth, endOfMonth } = currentMonthDates;

    setStartDateForParam(startOfMonth);
    setEndDateForParam(endOfMonth);
    setStartDate('');
    setEndDate('');
  };

  const hnadleClickTodayDate = () => {
    const { startDate, endDate } = getTodayStartAndEndDates();

    setStartDateForParam(startDate);
    setEndDateForParam(endDate);
    setStartDate('');
    setEndDate('');
  };

  const handleClickSearchDataReset = () => {
    setStartDateForParam('');
    setEndDateForParam('');
    setStartDate('');
    setEndDate('');
    setActivePeriodButton(0);
    setSearchOption('none');
    setPageNumber(0);
    setSearchKeyword('');
    setSearchState(selectSearch[0]);

    setTimeout(() => {
      requestSalesData();
    }, 500);
  };

  const handleClickSearchData = () => {
    setPageNumber(0);

    setTimeout(() => {
      requestSalesData();
    }, 500);
  };

  const handleClickSelectStartDate = (e: any, s: any) => {
    setStartDateForParam(convertToStartDate(s));
    setStartDate(s);
  };

  const handleClickSearchOption = (item: string | number) => {
    setSearchOption(item);

    const transferValue =
      item === 'none' ? 0 : item === 'manager' ? 1 : item === 'cardname' ? 2 : 3;

    setSearchState(selectSearch[transferValue]);
  };

  const handleClickSelectEndDate = (e: any, s: any) => {
    setEndDateForParam(convertToEndDate(s));
    setEndDate(s);
  };

  const handleClickRequestPeriodDate = (index: number) => {
    setActivePeriodButton(index);

    if (index === 2) {
      handleClickThisMonthDate();
    } else if (index === 1) {
      hnadleClickTodayDate();
    } else {
      handleClickTotalDate();
    }

    setTimeout(() => {
      requestSalesData();
    }, 500);
  };

  useEffect(() => {
    if (companyInfo.companyidx !== 0) {
      requestSalesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo.companyidx, pageNumber]);

  useEffect(() => {
    if (endDate) {
      setActivePeriodButton(4);
    }
  }, [endDate]);

  return (
    <Wrapper>
      <SalesDetails
        startDate={startDate}
        endDate={endDate}
        salesList={salesList}
        activePeriodButton={activePeriodButton}
        pageNumber={pageNumber}
        placeholder={searchState.text}
        searchOption={searchOption}
        selectSearchOptions={selectSearchOptions}
        searchKeyword={searchKeyword}
        totalElements={totalElements}
        totalPages={totalPages}
        currentPage={currentPage}
        pageSize={pageSize}
        onChangePageNumber={setPageNumber}
        onChangeStartDate={handleClickSelectStartDate}
        onChangeEndDate={handleClickSelectEndDate}
        onChangeSearchKeyword={handleChangeSearchKeyword}
        onClickReset={handleClickSearchDataReset}
        onClickSelectPeriod={handleClickRequestPeriodDate}
        onClickGetSalesList={handleClickSearchData}
        onClickSearchOption={handleClickSearchOption}
      />

      <MonthlySalesDetails companyInfo={companyInfo} />
    </Wrapper>
  );
};

export default Sales;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;

  h2 {
    color: ${color.black2};
    ${font.h2_bold};
  }

  table tbody td p i {
    color: red;
  }
`;
